// Copyright (c) Bentley Systems
import { useCallback, useEffect, useRef, useState } from "react";
import { AppDispatch } from "../app/store";
import { ConfigManager } from "../Helpers/config-manager";
import { setErrorState } from "../Helpers/helper-functions";
import { ITwinRoutingOverview } from "./Models/on-prem-routing-models";

type UseOnPremRoutingApiProps = {
  accessToken: string;
  dispatch: AppDispatch;
  isNodeAllowed: boolean;
};

// ── Raw API payload types ─────────────────────────────────────────────────────

type RawUsageRecord = {
  systemId: string;
  projectId: string;
  projectName: string;
  timesUsed: number;
  lastTimeUsed: string;
};

type RawMqttClient = {
  mqttClientName: string;
  isUpgraded: boolean;
  upgradedAt?: string;
  upgradedByName?: string;
  usages?: RawUsageRecord[];
  systemUsages?: RawUsageRecord[]; // backend DTO alias
};

type RawITwin = {
  iTwinId: string;
  mqttClients?: RawMqttClient[];
};

// ── Pure mapping function (module-level — no hook-state dependencies) ─────────

const mapOverviewResponse = (payload: unknown): ITwinRoutingOverview[] => {
  const source = Array.isArray(payload)
    ? (payload as RawITwin[])
    : ((payload as { iTwins?: RawITwin[] } | null)?.iTwins ?? []);

  return source.map((iTwin) => ({
    iTwinId: iTwin.iTwinId,
    mqttClients: (iTwin.mqttClients ?? []).map((client) => ({
      mqttClientName: client.mqttClientName,
      isUpgraded: client.isUpgraded,
      upgradedAt: client.upgradedAt,
      upgradedByName: client.upgradedByName,
      // Backend DTO uses `systemUsages`; UI expects `usages`.
      usages: client.usages ?? client.systemUsages ?? [],
    })),
  }));
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useOnPremRoutingApi = ({
  accessToken,
  dispatch,
  isNodeAllowed,
}: UseOnPremRoutingApiProps) => {
  const [iTwins, setITwins] = useState<ITwinRoutingOverview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isToggling, setIsToggling] = useState<string | null>(null);
  const toggleAbortRef = useRef<AbortController | null>(null);

  // Cancel any in-flight toggle on unmount.
  useEffect(() => {
    return () => {
      toggleAbortRef.current?.abort();
    };
  }, []);

  const fetchOverview = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      const runId = crypto.randomUUID();
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremRouting/overview`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }

        const data = await response.json();
        const mapped = mapOverviewResponse(data);

        // Fetch iTwin display names in parallel; errors are silently ignored so
        // the page still renders even if the platform API is unavailable.
        const nameResults = await Promise.allSettled(
          mapped.map(async (iTwin) => {
            const res = await fetch(
              `${ConfigManager.iTwinPlatformUrl}/itwins/${iTwin.iTwinId}`,
              {
                headers: {
                  Authorization: accessToken,
                  RunId: runId,
                },
                signal,
              },
            );
            if (!res.ok) return null;
            const json = (await res.json()) as { iTwin?: { displayName?: string } };
            return json.iTwin?.displayName
              ? { id: iTwin.iTwinId, name: json.iTwin.displayName }
              : null;
          }),
        );

        const nameMap = new Map<string, string>(
          nameResults
            .filter(
              (r): r is PromiseFulfilledResult<{ id: string; name: string }> =>
                r.status === "fulfilled" && r.value !== null,
            )
            .map((r) => [r.value.id, r.value.name]),
        );

        setITwins(
          mapped.map((iTwin) => ({
            ...iTwin,
            displayName: nameMap.get(iTwin.iTwinId),
          })),
        );
      } catch (error: unknown) {
        if ((error as { name?: string })?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, dispatch],
  );

  useEffect(() => {
    if (!isNodeAllowed) {
      return;
    }
    const abortController = new AbortController();
    void fetchOverview(abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [fetchOverview, isNodeAllowed]);

  const toggleMqttClient = useCallback(
    async (mqttClientName: string): Promise<boolean> => {
      toggleAbortRef.current?.abort();
      const abortController = new AbortController();
      toggleAbortRef.current = abortController;

      setIsToggling(mqttClientName);
      const runId = crypto.randomUUID();
      const encodedName = encodeURIComponent(mqttClientName);
      const url = `${ConfigManager.projectProviderUrl}/api/OnPremRouting/clients/${encodedName}/toggle`;

      try {
        const response = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return false;
        }

        await fetchOverview();
        return true;
      } catch (error: unknown) {
        if ((error as { name?: string })?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
        return false;
      } finally {
        setIsToggling(null);
      }
    },
    [accessToken, dispatch, fetchOverview],
  );

  return {
    iTwins,
    isLoading,
    isToggling,
    fetchOverview,
    toggleMqttClient,
  };
};
